import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack, AccountBalance, Warning, CheckCircle, Assignment, Search, Refresh } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';

const TerminalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [terminalData, setTerminalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the return state from navigation (search, page, expanded panels)
  const returnState = location.state || {};

  // Filters - removed groupBy
  const [filters, setFilters] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date()
  });

  const { getTerminalDetails } = useApi();

  const loadTerminalDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        startDate: format(filters.startDate, 'yyyy-MM-dd'),
        endDate: format(filters.endDate, 'yyyy-MM-dd')
      };

      const response = await getTerminalDetails(id, params);
      setTerminalData(response);
    } catch (err) {
      setError(err.message || 'Failed to load terminal details');
    } finally {
      setLoading(false);
    }
  }, [id, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (id) {
      loadTerminalDetails();
    }
  }, [id, loadTerminalDetails]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatRTA = (accountNumber) => {
    if (!accountNumber || accountNumber.length < 4) {
      return accountNumber || 'N/A';
    }
    // Format as x0000 - single x followed by last 4 digits
    const lastFour = accountNumber.slice(-4);
    return `x${lastFour}`;
  };

  // Handle back navigation - preserve search state and expand the terminal's location
  const handleBack = useCallback(() => {
    const { returnSearch, returnPage, returnExpandedPanels, terminalMarket, terminalLocation } = returnState;

    // Auto-expand the market/store that contains this terminal
    const expandedPanels = { ...(returnExpandedPanels || {}) };
    const market = terminalMarket || terminalData?.market;
    const location = terminalLocation || terminalData?.locationName;

    if (market) {
      expandedPanels[market] = true;
    }
    if (location) {
      // Also expand the store within the market
      const storeKey = `${market || 'Other'}|${location}`;
      expandedPanels[storeKey] = true;
      // For legacy locations without market
      expandedPanels[location] = true;
    }

    navigate('/terminals', {
      state: {
        preserveSearch: true,
        searchTerm: returnSearch || '',
        page: returnPage || 0,
        expandedPanels,
        expandedStoreName: location
      }
    });
  }, [navigate, returnState, terminalData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error}
        </Alert>
        <Box mt={2}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            Back to Terminals
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              Back to Terminals
            </Button>
            <Typography variant="h4">
              Terminal {terminalData?.terminalId} - Settlement Details
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              startIcon={<Assignment />}
              onClick={() => navigate(`/terminals/${id}/transactions`)}
              variant="contained"
              color="primary"
            >
              View Transactions
            </Button>
            <Button
              startIcon={<Refresh />}
              onClick={loadTerminalDetails}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {terminalData?.locationName && (
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {terminalData.locationName}
          </Typography>
        )}

        {/* Consolidation Note for PAI terminals */}
        {terminalData?.consolidatedByLocation && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {terminalData.consolidationNote}
            </Typography>
            {terminalData.locationTerminals && (
              <Box mt={1}>
                <Typography variant="caption" display="block">
                  Includes terminals: {terminalData.locationTerminals.join(', ')}
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Date Range
            </Typography>
            <Grid container spacing={2} alignItems="end">
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={loadTerminalDetails}
                  disabled={loading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Settlement Summary Section */}
        {terminalData?.settlementSummary && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Settlement Summary
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Total Transactions
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {formatNumber(terminalData.settlementSummary.totalTransactions)}
                    </Typography>
                  </Box>
                </Grid>
                
                {terminalData.settlementSummary.vaultAccount && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Vault Account
                      </Typography>
                      <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                        {formatRTA(terminalData.settlementSummary.vaultAccount.rta)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {terminalData.settlementSummary.vaultAccount.owner}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Total Withdrawals
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {formatCurrency(terminalData.settlementSummary.withdrawalAmount)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      (vault deposits)
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Total Surcharge
                    </Typography>
                    <Typography variant="h5" color="info.main">
                      {formatCurrency(terminalData.settlementSummary.surchargeAmount || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      (split accounts)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Settlements List - shown for all users */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Settlement History
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              "Total" is the combined common-ID total (calendar day).
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Settlement Date</strong></TableCell>
                    <TableCell align="right"><strong>Transactions</strong></TableCell>
                    <TableCell align="right"><strong>Dispensed</strong></TableCell>
                    <TableCell align="right"><strong>Surcharge</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {terminalData?.dailyBreakdown?.length > 0 ? (
                    terminalData.dailyBreakdown.map((day, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{day.SettlementDate}</TableCell>
                        <TableCell align="right">{formatNumber(day.TransactionCount)}</TableCell>
                        <TableCell align="right">{formatCurrency(day.Amount || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(day.SurchargeAmount || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency((day.Amount || 0) + (day.SurchargeAmount || 0))}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">No settlements found in this date range</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {terminalData?.dailyBreakdown?.length > 0 && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Showing {terminalData.dailyBreakdown.length} settlement{terminalData.dailyBreakdown.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Admin-only sections */}
        {isAdmin && (
        <Grid container spacing={3}>
        {/* Account Holders Section - Admin Only */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Holders
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Account Holder</strong></TableCell>
                      <TableCell align="center"><strong>RTA</strong></TableCell>
                      <TableCell align="right"><strong>Transactions</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {terminalData?.accountHolders?.map((holder, index) => (
                      <TableRow key={index}>
                        <TableCell>{holder.AccountHolder}</TableCell>
                        <TableCell align="center">{formatRTA(holder.RTA)}</TableCell>
                        <TableCell align="right">{formatNumber(holder.TransactionCount)}</TableCell>
                        <TableCell align="right">{formatCurrency(holder.Amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Daily Breakdown Section - Admin Only */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Breakdown (Recent)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell align="right"><strong>Transactions</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {terminalData?.dailyBreakdown?.slice(-10).map((day, index) => (
                      <TableRow key={index}>
                        <TableCell>{day.SettlementDate}</TableCell>
                        <TableCell align="right">{formatNumber(day.TransactionCount)}</TableCell>
                        <TableCell align="right">{formatCurrency(day.Amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {terminalData?.dailyBreakdown?.length > 10 && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Showing last 10 days of {terminalData.dailyBreakdown.length} total days
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Surcharge Splits Section */}
        {terminalData?.surchargeSplits && (
          <Grid item xs={12}>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <AccountBalance sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Surcharge Account Splits
                    </Typography>
                  </Box>
                  {terminalData.surchargeSplits.terminalSurcharge && (
                    <Box display="flex" alignItems="center" ml={2}>
                      <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
                        Terminal Surcharge: {formatCurrency(terminalData.surchargeSplits.terminalSurcharge.amountDollars)}
                      </Typography>
                      {terminalData.surchargeSplits.hasPartialVisibility ? (
                        <Chip
                          label="Balance N/A (Limited View)"
                          color="default"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={terminalData.surchargeSplits.isBalanced ? <CheckCircle /> : <Warning />}
                          label={terminalData.surchargeSplits.isBalanced ? 'Balanced' : 'Unbalanced'}
                          color={terminalData.surchargeSplits.isBalanced ? 'success' : 'warning'}
                          size="small"
                        />
                      )}
                    </Box>
                  )}
                </Box>

                {terminalData.surchargeSplits.accountSplits?.length > 0 ? (
                  <Box>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Account Name</strong></TableCell>
                            <TableCell><strong>RTA</strong></TableCell>
                            <TableCell align="right"><strong>Split Amount</strong></TableCell>
                            <TableCell align="right"><strong>Percentage</strong></TableCell>
                            <TableCell align="center"><strong>Share Visualization</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {terminalData.surchargeSplits.accountSplits.map((split, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {split.accountName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {formatRTA(split.accountNumber)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium">
                                  {formatCurrency(split.splitAmount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {split.splitPercentage.toFixed(1)}%
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ width: 120 }}>
                                <Box sx={{ width: '100%' }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(split.splitPercentage, 100)}
                                    sx={{
                                      height: 8,
                                      borderRadius: 4,
                                      backgroundColor: '#e0e0e0',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        backgroundColor: split.splitPercentage > 25 ? '#4caf50' :
                                                       split.splitPercentage > 10 ? '#2196f3' :
                                                       '#ff9800'
                                      }
                                    }}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Summary Row */}
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="textSecondary">
                            Total Accounts
                          </Typography>
                          <Typography variant="h6">
                            {terminalData.surchargeSplits.accountSplits.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="textSecondary">
                            Total Split Amount
                          </Typography>
                          <Typography variant="h6">
                            {formatCurrency(terminalData.surchargeSplits.totalSplitAmount)}
                          </Typography>
                        </Grid>
                        {terminalData.surchargeSplits.terminalSurcharge && (
                          <>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="textSecondary">
                                Difference
                              </Typography>
                              <Typography 
                                variant="h6" 
                                color={terminalData.surchargeSplits.hasPartialVisibility ? 'text.secondary' : 
                                       terminalData.surchargeSplits.isBalanced ? 'success.main' : 'warning.main'}
                              >
                                {terminalData.surchargeSplits.hasPartialVisibility ? 'N/A' : 
                                 formatCurrency(
                                  Math.abs(terminalData.surchargeSplits.totalSplitAmount - 
                                          terminalData.surchargeSplits.terminalSurcharge.amountDollars)
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Typography variant="body2" color="textSecondary">
                                Status
                              </Typography>
                              <Box display="flex" alignItems="center">
                                {terminalData.surchargeSplits.hasPartialVisibility ? (
                                  <Chip
                                    label="N/A (Limited View)"
                                    color="default"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    icon={terminalData.surchargeSplits.isBalanced ? <CheckCircle /> : <Warning />}
                                    label={terminalData.surchargeSplits.isBalanced ? 'Balanced' : 'Needs Review'}
                                    color={terminalData.surchargeSplits.isBalanced ? 'success' : 'warning'}
                                    size="small"
                                  />
                                )}
                              </Box>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                ) : (
                  <Box textAlign="center" py={3}>
                    <Typography color="textSecondary">
                      No surcharge splits configured for this terminal
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Account splits need to be imported to view surcharge distribution
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        </Grid>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default TerminalDetails;